// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import usePopup from "@/hooks/usePopup";
import type { TyStatusLock, TyUnit, TyUnitStatus } from "@/types";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import "../stepper.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    unit: TyUnit;
    /** The live paperwork that owns the status right now, if any. Set makes the whole trail a picture. */
    statusLock?: TyStatusLock;
    /** Moves the unit. Absent leaves the trail a picture rather than a control. */
    onPick?: (status: TyUnitStatus) => void;
    disabled?: boolean;
};

/**
 * Where a unit has got to on its way off the shelf: **available → reserved → sold**, or *rented*
 * where the unit is offered for rent.
 *
 * It replaces a chip in the header that said one word. A chip can report a status but never show
 * what comes next, and "what happens to this flat now" is the only reason anybody opens a unit twice.
 * It sits above the tabs rather than inside one, because it is true whichever tab you are standing
 * on — the same band the holder card and the lock note already live in.
 *
 * **Three of the four steps are not status changes at all.** A unit's status is *derived*: it is
 * `reserved` because a reservation holds it and `sold` because a contract does, and the flows that
 * write those are the only things that may write the status with them. So pressing `reserved` or a
 * terminal hands the step to the caller, which opens the form asking **who** and for how much —
 * exactly as pressing `won` on a lead asks which of a reservation and a contract happened.
 *
 * Only `available` is a plain move: it is a correction to a unit nothing live is holding, and it is
 * confirmed here rather than in the screen that draws the trail, so no caller can forget to. It is
 * also the **only** way back — a sold unit does not step down to `reserved`, because no act does
 * that; the way back from a contract is to cancel it.
 *
 * **Nothing is clickable while sales owns the status.** A live reservation or contract is the thing
 * that decided it, and a unit that disagreed with its own paperwork is the one state nothing in the
 * product could explain — so the trail reports rather than offering a move the API would refuse. It
 * does not explain itself in a sentence underneath: the holder badge is directly below it, naming
 * the person and the paper, which is the same answer with the facts in it.
 */
export default function UnitStepper({ unit, statusLock, onPick, disabled }: Props) {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);

    /**
     * A unit leaves the shelf one way or the other, never both, so the trail draws the end that
     * belongs to it — the one its offer names, or the one it has already reached when the two
     * disagree, which is how a unit let out after being listed for sale still draws a whole trail.
     */
    const terminal: TyUnitStatus = unit.status === "rented" || (unit.status !== "sold" && unit.offer === "rent") ? "rented" : "sold";
    const flow: TyUnitStatus[] = ["available", "reserved", terminal];

    const at = flow.indexOf(unit.status);

    const confirm = (status: TyUnitStatus) =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.properties.moveStatus,
                message: t.properties.confirmStatus.replace("{status}", t.properties.statuses[status]),
                confirmLabel: t.properties.moveStatus,
                danger: false,
                onConfirm: () => onPick?.(status),
            },
        });

    return (
        <div className="unit-stepper">
            <div className="stepper">
                {flow.map((step, index) => {
                    const state = index < at ? "done" : index === at ? "now" : "next";
                    /**
                     * Forward, or back to the shelf — and nothing else. A sold unit cannot step back
                     * to `reserved`: there is no act that does that, the way back is to cancel the
                     * contract, and a step that is not a legal move is dead rather than refused.
                     */
                    const isLegal = !!onPick && !disabled && !statusLock && index !== at && (index > at || step === "available");

                    return (
                        <button
                            key={step}
                            type="button"
                            className="stepper__step"
                            data-state={state}
                            disabled={!isLegal}
                            onClick={() => {
                                // Going back to the shelf is a move; everything forward is paperwork.
                                if (step === "available") confirm(step);
                                else onPick?.(step);
                            }}
                        >
                            <span className="stepper__dot" aria-hidden="true" />
                            <span className="stepper__label">{t.properties.statuses[step]}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
